// The registry of feature flags (CHT-769). A CLOSED list: a key that is not here does not exist, whatever a flag service answers.
//
// Rules:
// - Every flag has a safe default. The default is what a customer gets when the flag service is off, slow, wrong or unknown.
// - A flag changes a customer EXPERIENCE (layout, a new screen, a model or prompt rollout). It never changes money, safety or
//   consent. The closed `affects` list has no such value, a test refuses a registry that names one, and the key of a flag may not
//   name one either.
// - `evaluation: "server"` means a decision of the server. A client flag (`"client"`) may only choose between experiences; the server
//   never trusts a client flag for an authorization, a payment or a consent decision (and no such flag may exist).
// - A value is a variant name. A flag with two states uses `off` and `on`.
export const featureFlagAffects = Object.freeze([
    "layout",
    "copy_style",
    "new_screen",
    "ranking_order",
    "model_rollout",
    "prompt_rollout",
]);
export const featureFlagKinds = Object.freeze(["rollout", "experiment", "kill_switch"]);
export const FEATURE_FLAGS = {
    agent_prompt_rollout: {
        kind: "rollout",
        affects: "prompt_rollout",
        evaluation: "server",
        variants: ["control", "candidate"],
        default: "control",
        exposure: true,
        description: "Which concierge prompt version a conversation uses. The control is the version in code.",
    },
    itinerary_timeline_ui: {
        kind: "rollout",
        affects: "new_screen",
        evaluation: "client",
        variants: ["off", "on"],
        default: "off",
        exposure: false,
        description: "Shows the new itinerary timeline screen.",
    },
    home_recommendation_layout: {
        kind: "experiment",
        affects: "layout",
        evaluation: "client",
        variants: ["control", "compact"],
        default: "control",
        exposure: true,
        description: "Compares two layouts of the recommendation cards on Home.",
    },
    customer_experience_kill_switch: {
        kind: "kill_switch",
        affects: "new_screen",
        evaluation: "server",
        variants: ["off", "on"],
        default: "off",
        exposure: false,
        description: "Turns off the newest customer experiences without an app release. `on` hides them.",
    },
    // CHT-780: the beta cohort. Read by the server only, and only while the operator control COHORT_GATE is enforced and the cohort source
    // is `flag` (LAFAYETTE_BETA_COHORT_SOURCE). The default `out` is the safe state: a customer outside the cohort is served by a person.
    beta_cohort: {
        kind: "rollout",
        affects: "model_rollout",
        evaluation: "server",
        variants: ["out", "member"],
        default: "out",
        exposure: false,
        description: "Whether the customer is in the beta cohort that receives the AI customer features.",
    },
};
export const featureFlagKeys = Object.freeze(Object.keys(FEATURE_FLAGS));
export const isFeatureFlagKey = (value) => Object.hasOwn(FEATURE_FLAGS, value);
export const featureFlagDefinition = (key) => FEATURE_FLAGS[key];
/** The flags a client may read through the flags route. A server flag never reaches a client. */
export const clientFeatureFlagKeys = Object.freeze(featureFlagKeys.filter((key) => FEATURE_FLAGS[key].evaluation === "client"));
/** Words that no flag key or description may name: a flag never touches these. */
const FORBIDDEN_FLAG_WORDS = [
    "payment",
    "pay",
    "price",
    "pricing",
    "amount",
    "refund",
    "quote",
    "money",
    "safety",
    "safe",
    "consent",
    "privacy",
    "opt_out",
    "optout",
    "auth",
    "authorization",
    "permission",
];
/**
 * Checks a registry. The shipped registry must give no problem (a test runs it). A test also runs a bad registry, to prove the
 * checks catch a flag that names money, safety or consent.
 */
export function checkFeatureFlagRegistry(registry) {
    const problems = [];
    for (const [key, definition] of Object.entries(registry)) {
        if (!/^[a-z][a-z0-9_]{2,63}$/.test(key))
            problems.push({ key, problem: "BAD_KEY" });
        if (definition.variants.length === 0)
            problems.push({ key, problem: "NO_VARIANTS" });
        if (!definition.variants.includes(definition.default)) {
            problems.push({ key, problem: "DEFAULT_NOT_A_VARIANT" });
        }
        if (!featureFlagAffects.includes(definition.affects)) {
            problems.push({ key, problem: "BAD_AFFECTS" });
        }
        const words = `${key} ${definition.description}`
            .toLowerCase()
            .replace(/[^a-z]+/g, " ")
            .split(" ");
        const named = FORBIDDEN_FLAG_WORDS.some((word) => word.includes("_") ? `${key}`.includes(word) : words.includes(word));
        if (named)
            problems.push({ key, problem: "FORBIDDEN_TOPIC" });
    }
    return problems;
}
